import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "./schema";
import { getToken, setToken } from "../lib/session";

export type Schemas = components["schemas"];
export type Meal = Schemas["Meal"];
export type DaySummary = Schemas["DaySummary"];
export type Entry = Schemas["EntryOut"];
export type EntryIn = Schemas["EntryIn"];
export type Food = Schemas["FoodOut"];
export type Goal = Schemas["GoalOut"];
export type User = Schemas["UserOut"];

// Empty means same origin (e.g. Caddy serving the app and proxying /api).
const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  /** HTTP status, or 0 when the server couldn't be reached. */
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** No response: offline, a dropped connection, or the request timed out. */
export function isNetworkError(e: unknown): boolean {
  return e instanceof ApiError ? e.status === 0 : e instanceof TypeError || isAbort(e);
}

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && (e.name === "AbortError" || e.name === "TimeoutError");
}

// On a weak connection requests tend to hang rather than fail, so give up after a while.
export const REQUEST_TIMEOUT_MS = Number(import.meta.env.VITE_REQUEST_TIMEOUT_MS ?? 15_000);

function fetchWithTimeout(request: Request): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new DOMException("Request timed out", "TimeoutError")),
    REQUEST_TIMEOUT_MS,
  );
  request.signal.addEventListener("abort", () => controller.abort(request.signal.reason));
  return fetch(new Request(request, { signal: controller.signal })).finally(() =>
    clearTimeout(timer),
  );
}

/** Turns FastAPI's `detail` (a string, or a list of validation errors) into one message. */
export function errorMessage(body: unknown, status: number): string {
  const detail = (body as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: { loc?: unknown[]; msg?: string }) => {
        const field = (d.loc ?? []).filter((p) => p !== "body").join(".");
        return field ? `${field}: ${d.msg}` : d.msg;
      })
      .join("; ");
  }
  return `Request failed (${status})`;
}

const auth: Middleware = {
  onRequest({ request }) {
    const token = getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  onResponse({ request, response }) {
    // An expired or revoked session: drop it so the app shows the login page.
    if (response.status === 401 && request.headers.has("Authorization")) setToken(null);
    return response;
  },
};

export const api = createClient<paths>({ baseUrl, fetch: fetchWithTimeout });
api.use(auth);

/** Awaits an openapi-fetch call and returns its data, throwing ApiError on failure. */
export async function call<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: Awaited<typeof request>;
  try {
    result = await request;
  } catch (e) {
    if (isNetworkError(e)) throw new ApiError("No connection. Try again when you're online.", 0);
    throw e;
  }
  const { data, error, response } = result;
  if (!response.ok) throw new ApiError(errorMessage(error, response.status), response.status);
  return data as T;
}
